<template>
  <div>
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
    </div>

    <!---JSON code-->
    <div class="json-action">
      <v-btn @click="collapseJsonPreview">
        <v-icon> mdi-code-json </v-icon>
      </v-btn>
    </div>

    <div v-if="displayJsonPreview" class="json-preview">
      <div class="json-preview__header">
        <div v-if="jsonFormatError" class="d-flex align-center mb-3">
          <span class="json-preview__format-error">Invalid JSON format!</span>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn v-on="on" @click="undoJson" class="ml-3">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </template>
            <p>Undo to previous valid JSON state</p>
          </v-tooltip>
        </div>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        :key="jsonInputKey"
        v-model="quizDataJsonFormat"
        :title="''"></code-input>
    </div>
    <!---JSON code END -->

    <div class="panzoom-parent">
      <div ref="panzoom">
        <!--Quiz settings -->
        <div class="d-flex justify-center">
          <div class="quiz-data">
            <div class="multi-step-quiz-v4-section spacing-bottom--24">
              <h2 class="section-title spacing-bottom--12">{{ $t("shortcodes.multiStepQuiz.appearance") }}</h2>
              <div class="appearance-input-wrapper">
                <VaMediaObjectFileInput
                  :label="$t('shortcodes.multiStepQuiz.agent_avatar')"
                  class="quiz-image-input"
                  :item-id="quizData.imageID"
                  @update:photo="updateAvatar($event)" />
                <VaColorPicker
                  class="color-picker-input panzoom-ignore"
                  position="bottom"
                  :title="$t('resources.domains.primary_colour')"
                  v-model="quizData.primaryColor"></VaColorPicker>
              </div>
            </div>
            <hr class="quiz-hr" />
            <div class="multi-step-quiz-v4-section spacing-bottom--24">
              <h2 class="section-title spacing-bottom--4">{{ $t("shortcodes.multiStepQuiz.headline") }}</h2>
              <div class="section-subtitle">{{ $t("shortcodes.multiStepQuiz.headline_subtitle") }}</div>
              <va-tinymce-input
                ref="tinymce"
                class="w-full include-border"
                :current-content="quizData.headline.content"
                :editor-height="300"
                stripped-down
                @update="updateHeadlineContent" />
            </div>
            <div v-if="!quizData.questionsTree" class="d-flex justify-center">
              <v-btn :color="'primary'" class="mr-2" @click="addQuestion">Add question</v-btn>
            </div>
          </div>
        </div>
        <!--Quiz settings END-->
        <!--Questions -->
        <div v-if="quizData.questionsTree" class="quiz-tree">
          <QuizTree :key="treeKey" :question-tree="quizData.questionsTree"></QuizTree>
        </div>
        <!--Questions END -->
      </div>
    </div>
  </div>
</template>

<script>
  // Components
  import QuizTree from "@/components/shortcodes/multiStepQuizV4/QuizTree.vue";
  import Panzoom from "@panzoom/panzoom";
  import CodeInput from "@/components/custom/CodeInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import { Tree, constructTreeFromObject } from "@/components/shortcodes/multiStepQuizV4/QuizTree.js";
  // Helpers
  import cloneDeep from "lodash/cloneDeep";
  // Constants
  import { QUESTION } from "@/components/shortcodes/multiStepQuizV4/constant";
  import definedAbility from "@/ability";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { convertAssetsPathToRelativeLinks, convertAssetsPathToAbsoluteLinks } from "@/utility/convertAssetsPaths";

  export default {
    name: "MultiStepQuizV4Dialog",
    components: { QuizTree, CodeInput, VaColorPicker },
    data() {
      return {
        ability: definedAbility(),
        panzoomInstance: null,
        quizData: {
          imageID: "",
          imageURL: "",
          headline: {
            content: "",
          },
          primaryColor: "#0D6EFD",
          questionsTree: null,
        },
        /**
         * When editing JSON input (code-input), user might make the JSON inside of the code-input invalid;
         * jsonFormatError is true if JSON format inside of the code-input is incorrect (not parseable using JSON.parse)
         */
        displayJsonPreview: false,
        quizDataJson: "",
        jsonFormatError: false,
        jsonInputKey: 0,
        treeKey: 0,
      };
    },
    computed: {
      quizDataJsonFormat: {
        get() {
          return this.quizDataJson;
        },
        set(value) {
          let parsedJson;
          try {
            this.jsonFormatError = false;
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
          }

          if (!this.jsonFormatError) {
            /**
             * Since the data was json encoded, we don't have all the tree methods like addChild, removeNode, etc.
             * We have to convert JS object to tree instance.
             */
            this.quizData.imageID = parsedJson.imageID;
            this.quizData.imageURL = parsedJson.imageURL;
            if (this.$refs.tinymce) {
              this.$refs.tinymce.updateContent(parsedJson.headline.content);
            }
            this.quizData.primaryColor = parsedJson.primaryColor;
            this.quizData.questionsTree = constructTreeFromObject(parsedJson.questionsTree);
          }
        },
      },
    },
    watch: {
      quizData: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        /**
         * Ignores all panzoom events on elements with provided class;
         * This is required because sometimes we use mouse click and drag to select things (text in the input for example)
         * This prevents panzoom from moving the view when trying to select text
         */
        excludeClass: "panzoom-ignore",
        /**
         * By default, panzoom will call event.preventDefault() and event.stopPropagation() on every pan event.
         * This prevents colorpicker from being closed on outside click.
         * Override the default handleStartEvent function to solve this.
         */
        handleStartEvent: () => {},
      });

      // Set appropriate height for panzoom wrapper. Set ti to window innerheight
      this.$refs.panzoom && (this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`);

      const parent = this.$refs.panzoom.parentElement;

      parent.addEventListener("wheel", event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      });
    },
    methods: {
      updateHeadlineContent(content) {
        this.quizData.headline.content = content;
      },
      addQuestion() {
        this.quizData.questionsTree = new Tree(cloneDeep(QUESTION));
      },
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      updateAvatar(value) {
        if (value) {
          this.quizData.imageID = value["@id"];
          this.quizData.imageURL = value.contentUrl;
        } else {
          this.quizData.imageID = "";
          this.quizData.imageURL = "";
        }
      },
      collapseJsonPreview() {
        this.displayJsonPreview = !this.displayJsonPreview;
        // Only setup json data when json previewer is opened
        if (this.displayJsonPreview) {
          this.quizDataJson = JSON.stringify(this.quizData, null, " ");
        }
      },
      /**
       * Updates the JSON input to previous valid state.
       */
      undoJson() {
        const displayData = this.quizData;
        this.quizDataJsonFormat = JSON.stringify(displayData, null, " ");
        this.jsonInputKey++;
      },
      updateValue(val) {
        const stringifiedData = JSON.stringify(val, (key, value) => {
          if (key === "content") {
            // Replacing &emsp; with space, cause innerHTML doesn't deserialize from space to $emsp;
            value = value.replace(/&emsp;/g, " ");
            let parser = new DOMParser();
            // Creating document from string to run dynamic component serialization which uses DOM manipulation
            let document = parser.parseFromString(value, "text/html");
            serializeDynamicComponents(document);
            // Selecting only the body content of the document as the wrappers are not needed
            document = document.querySelector("body").innerHTML;
            document = convertAssetsPathToRelativeLinks(document)?.querySelector("body")?.innerHTML;

            return window.btoa(encodeURIComponent(document));
          }

          return value;
        });

        window.parent.postMessage(
          {
            mceAction: "updateMultiStepQuizV4Data",
            data: stringifiedData,
          },
          origin,
        );
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === "sendData") {
            const { data } = message.data;

            if (data !== "undefined") {
              const jsonData = JSON.parse(data, (key, value) => {
                if (key === "content") {
                  return convertAssetsPathToAbsoluteLinks(decodeURIComponent(atob(value)));
                }

                return value;
              });

              /**
               * Since the data was json encoded, we don't have all the tree methods like addChild, removeNode, etc.
               * We have to convert JS object to tree instance.
               */
              this.quizData = jsonData;
              this.quizData.questionsTree = constructTreeFromObject(jsonData.questionsTree);
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";
  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }
  .json-action {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .quiz-data {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .quiz-tree {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  .json-preview {
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 5px;
    z-index: 101;
    background-color: get-color("element-bg-200");
    &__header {
      display: flex;
      background: get-color("element-bg-200");
      padding: 70px 20px 0;
      &-title {
        color: get-color("subtitle");
        @include text-body-1-bold();
      }
    }
    &__format-error {
      color: get-color("error");
      @include text-body-1-bold();
    }
  }
  .json-error {
    background-color: get-color("error");
  }
  .code-error {
    border: 3px dashed get-color("error") !important;
  }

  .quiz-hr {
    border: 1px solid get-color("border");
    position: relative;
    border-top: 0;
    margin: 0 -12px;
  }

  .quiz-image-input {
    width: 100%;

    ::v-deep {
      .image-input-block {
        background: get-color("same-white");
        padding-top: 9px;
        padding-bottom: 9px;

        button {
          background-color: get-color("same-white") !important;
          color: get-color("primary") !important;
          border: 1px solid get-color("primary") !important;
          cursor: pointer;

          input {
            cursor: pointer;
          }

          input[type="file"]::-webkit-file-upload-button {
            cursor: pointer;
          }

          &:hover {
            background-color: get-color("same-white") !important;
            color: get-color("primary") !important;
          }
        }
      }

      .image-input-label.text--primary {
        @include text-caption-bold();
        line-height: 14px !important;
        color: get-color("subtitle") !important;
        padding-bottom: 6px !important;
      }

      .fileType__error {
        @include text-caption();
        color: get-color("error");
        margin: 0;
      }
    }
  }

  .multi-step-quiz-v4-section {
    padding-top: 12px;
    padding-left: 12px;
    padding-right: 12px;

    .section-title {
      @include text-body-1-bold();
      color: get-color("body");
    }

    .section-subtitle {
      @include text-subtitle-2();
      color: get-color("subtitle");
      margin-bottom: 12px;
    }
  }

  .appearance-input-wrapper {
    display: flex;
    gap: 16px;

    .color-picker-input {
      ::v-deep {
        .v-input__slot {
          max-height: unset !important;

          input {
            padding-top: 16px;
            padding-bottom: 16px;
          }
        }
      }
    }
  }
</style>
